import { useState, useRef, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { speakingAPI, practiceAPI } from '../../api/client'
import { useExamFramework } from '../../hooks/useExamFramework'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

// Representative sample prompts keyed by part task_type (no per-part prompt API yet).
// Covers both VSTEP and IELTS task types (FR-029).
const PROMPTS: Record<string, string> = {
  // VSTEP
  social_interaction: 'Let\'s talk about your daily life. What do you usually do in your free time, and why do you enjoy it?',
  solution_discussion: 'A friend wants to improve their English quickly. Here are some options: join a class, use an app, or practise with a partner. Which is best and why?',
  topic_development: 'Present your views on this topic: "The benefits and drawbacks of working from home." Then be ready for follow-up questions.',
  // IELTS
  interview: 'Tell me about your hometown. What do you like most about living there?',
  cue_card: 'Describe a skill you would like to learn. You should say: what it is, why you want to learn it, how you would learn it, and how it would help you. You have 1 minute to prepare.',
  discussion: 'We\'ve been talking about skills. Let\'s discuss further: how has technology changed the way people learn new skills today?',
}

/**
 * FR-011 Speaking Training Module
 * Records audio → sends to Speaking Pipeline (3-tier AI assessment).
 * Parts are rendered dynamically per exam (FR-029).
 */
export default function SpeakingPage() {
  const { parts, partLabel, displayName, isLoading } = useExamFramework()
  const speakingParts = parts('speaking')
  const [activeCode, setActiveCode] = useState<string>('')
  const activePart = speakingParts.find(p => p.partCode === activeCode)

  useEffect(() => {
    if (!activeCode && speakingParts.length > 0) setActiveCode(speakingParts[0].partCode)
  }, [speakingParts, activeCode])

  // Per-part prompt from the bank (FR-029), falling back to a built-in sample.
  const { data: promptData } = useQuery({
    queryKey: ['speaking-prompt', activeCode],
    queryFn: () => practiceAPI.getPrompt('speaking', activeCode),
    enabled: !!activeCode,
  })
  const staticPrompt = (activePart && PROMPTS[activePart.taskType ?? '']) ||
    (activePart ? `${partLabel(activePart)} — please answer in English.` : 'Tell me about yourself and your English learning goals.')
  const promptText = (promptData?.source === 'db' && promptData.prompt) ? promptData.prompt : staticPrompt

  const [isRecording, setIsRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [result, setResult] = useState<any>(null)
  const mediaRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const submit = useMutation({
    mutationFn: (audioBlob: Blob) => {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'audio.webm')
      formData.append('meta', JSON.stringify({
        partType: activeCode || 'part1',
        questionPrompt: promptText,
        sessionId: 'placeholder-session-id',
      }))
      return speakingAPI.submit(formData)
    },
    onSuccess: setResult,
  })

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' })
      chunksRef.current = []
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data)
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        submit.mutate(blob)
        stream.getTracks().forEach(t => t.stop())
      }
      recorder.start()
      mediaRef.current = recorder
      setIsRecording(true)
      setSeconds(0)
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } catch {
      alert('Không thể truy cập microphone. Vui lòng cho phép quyền truy cập.')
    }
  }

  function stopRecording() {
    mediaRef.current?.stop()
    setIsRecording(false)
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#b41340', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>mic</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#b41340' }}>Speaking Coach</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          {activePart ? partLabel(activePart) : 'Speaking'} <span style={{ color: '#004be2', fontStyle: 'italic' }}>{displayName}</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          AI phân tích phát âm, độ lưu loát và nội dung câu trả lời của bạn.
        </p>
      </div>

      {/* LME-3: shadowing practice entry */}
      {!result && (
        <Link
          to="/speaking/shadowing"
          style={{
            display: 'flex', alignItems: 'center', gap: '1rem', textDecoration: 'none',
            background: 'linear-gradient(135deg, #0891b2, #67e8f9)', borderRadius: '1.25rem',
            padding: '1.125rem 1.5rem', marginBottom: '2rem', color: 'white',
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.75rem', fontVariationSettings: "'FILL' 1" }}>graphic_eq</span>
          <div style={{ flex: 1 }}>
            <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem' }}>Luyện Shadowing</H>
            <span style={{ fontSize: '0.8125rem', opacity: 0.9 }}>Nghe câu mẫu rồi nói nhại theo — cải thiện phát âm & nhịp điệu.</span>
          </div>
          <span className="material-symbols-outlined">arrow_forward</span>
        </Link>
      )}

      {/* Part selector (data-driven) */}
      {!result && speakingParts.length > 0 && (
        <div className="scroll-tabs" style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }}>
          {speakingParts.map(part => {
            const sel = activeCode === part.partCode
            return (
              <button
                key={part.partCode}
                onClick={() => setActiveCode(part.partCode)}
                style={{
                  flex: 1, minWidth: 0, padding: '0.875rem 1.25rem', borderRadius: '1rem',
                  border: sel ? 'none' : '1.5px solid #e8ecf8', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left',
                  background: sel ? 'linear-gradient(135deg, #b41340, #f74b6d)' : 'white',
                  boxShadow: sel ? '0 8px 20px rgba(180,19,64,0.25)' : '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, color: sel ? 'white' : '#272e42', lineHeight: 1.3 }}>{partLabel(part)}</H>
                {part.timeSeconds ? <span style={{ fontSize: '0.75rem', color: sel ? 'rgba(255,255,255,0.7)' : '#a5adc6', fontWeight: 600 }}>~{Math.round(part.timeSeconds / 60)} phút</span> : null}
              </button>
            )
          })}
        </div>
      )}

      {isLoading && !result ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>
      ) : !result ? (
        <div className="drill-2col" style={{ alignItems: 'start' }}>
          {/* Main recording panel */}
          <div style={{ background: 'white', borderRadius: '2rem', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,75,226,0.06)' }}>
            {/* Question card */}
            <div style={{ padding: '2.5rem', background: 'linear-gradient(135deg, #004be2, #809bff)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', bottom: '-2rem', right: '-2rem', width: '10rem', height: '10rem', background: 'rgba(142,0,213,0.3)', borderRadius: '50%', filter: 'blur(40px)' }} />
              <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.75rem' }}>
                Câu hỏi phỏng vấn
              </span>
              <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 800, color: 'white', lineHeight: 1.5, position: 'relative' }}>
                {promptText}
              </H>
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>timer</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8125rem', fontWeight: 600 }}>30–60 giây</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>mic</span>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8125rem', fontWeight: 600 }}>Tiếng Anh</span>
                </div>
              </div>
            </div>

            {/* Recording area */}
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              {/* Waveform visualization */}
              <div style={{ height: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', marginBottom: '2rem' }}>
                {Array.from({ length: 24 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: '0.25rem',
                      borderRadius: '9999px',
                      background: isRecording ? '#004be2' : '#d1dcff',
                      height: isRecording ? `${10 + Math.random() * 30}px` : '10px',
                      transition: 'height 0.2s, background 0.3s',
                      animation: isRecording ? `pulse-wave ${0.8 + (i % 4) * 0.2}s ease-in-out infinite alternate` : 'none',
                    }}
                  />
                ))}
              </div>

              {/* Timer */}
              {(isRecording || seconds > 0) && (
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2rem', fontWeight: 900, color: isRecording ? '#b41340' : '#535b71', marginBottom: '1.5rem' }}>
                  {formatTime(seconds)}
                </div>
              )}

              {/* Record button */}
              {!isRecording ? (
                <button
                  onClick={startRecording}
                  className="touch-target"
                  style={{
                    width: '6rem', height: '6rem', borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #b41340, #f74b6d)',
                    boxShadow: '0 0 0 8px rgba(180,19,64,0.1), 0 8px 24px rgba(180,19,64,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s', margin: '0 auto',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 0 12px rgba(180,19,64,0.12), 0 12px 32px rgba(180,19,64,0.4)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 0 0 8px rgba(180,19,64,0.1), 0 8px 24px rgba(180,19,64,0.35)' }}
                >
                  <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '2rem', fontVariationSettings: "'FILL' 1" }}>mic</span>
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="touch-target"
                  style={{
                    width: '6rem', height: '6rem', borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: '#272e42',
                    boxShadow: '0 0 0 8px rgba(39,46,66,0.1), 0 8px 24px rgba(39,46,66,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto', transition: 'all 0.2s',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ color: 'white', fontSize: '2rem', fontVariationSettings: "'FILL' 1" }}>stop</span>
                </button>
              )}

              <p style={{ marginTop: '1.5rem', color: '#535b71', fontSize: '0.875rem', fontWeight: 600 }}>
                {isRecording ? '● Đang ghi âm — Nhấn để dừng và gửi' : 'Nhấn để bắt đầu ghi âm'}
              </p>

              {submit.isPending && (
                <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  <div className="spinner" />
                  <p style={{ color: '#535b71', fontSize: '0.875rem' }}>Đang phân tích (tối đa 10 giây)...</p>
                </div>
              )}

              {submit.isError && (
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(180,19,64,0.08)', borderRadius: '1rem', color: '#b41340', fontSize: '0.875rem', fontWeight: 600 }}>
                  Không thể phân tích bài nói. Thử ghi âm lại nhé.
                </div>
              )}
            </div>
          </div>

          {/* Tips sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
              <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem' }}>AI sẽ đánh giá</H>
              {[
                { icon: 'spatial_audio_off', label: 'Phát âm', color: '#b41340', desc: 'Độ chính xác từng âm' },
                { icon: 'speed', label: 'Độ lưu loát', color: '#004be2', desc: 'Tốc độ và nhịp điệu' },
                { icon: 'book_2', label: 'Từ vựng', color: '#8e00d5', desc: 'Đa dạng và phù hợp' },
                { icon: 'rule', label: 'Ngữ pháp', color: '#006571', desc: 'Cấu trúc câu' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1rem' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem', background: `${item.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ color: item.color, fontSize: '1.25rem', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: '0.875rem' }}>{item.label}</span>
                    <span style={{ fontSize: '0.75rem', color: '#535b71' }}>{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
              <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1rem', color: '#004be2' }}>Mẹo trả lời tốt</H>
              {[
                'Nói rõ ràng, không quá nhanh',
                'Trả lời đủ 30–60 giây',
                'Dùng cấu trúc IDEA → REASON → EXAMPLE',
                'Tránh dừng quá lâu giữa câu',
              ].map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.625rem', marginBottom: '0.75rem' }}>
                  <span style={{ flexShrink: 0, width: '1.375rem', height: '1.375rem', borderRadius: '50%', background: '#004be2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 800 }}>{i + 1}</span>
                  <span style={{ fontSize: '0.8125rem', lineHeight: 1.5, color: '#535b71' }}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <SpeakingResult result={result} onReset={() => { setResult(null); setSeconds(0) }} />
      )}

      <style>{`
        @keyframes pulse-wave {
          from { height: 8px; opacity: 0.5; }
          to { height: 40px; opacity: 1; }
        }
      `}</style>
    </AppLayout>
  )
}

// ─── Speaking Result ──────────────────────────────────────────────────────────

function SpeakingResult({ result, onReset }: { result: any; onReset: () => void }) {
  const scores = [
    { label: 'Tổng điểm', value: result.content?.scoreOverall, color: '#004be2', icon: 'stars' },
    { label: 'Phát âm', value: result.pronunciation?.pronunciationScore, color: '#b41340', icon: 'spatial_audio_off' },
    { label: 'Lưu loát', value: result.pronunciation?.fluencyScore, color: '#8e00d5', icon: 'speed' },
    { label: 'Từ vựng', value: result.content?.scoreVocabulary, color: '#006571', icon: 'book_2' },
    { label: 'Ngữ pháp', value: result.content?.scoreGrammar, color: '#b45309', icon: 'rule' },
  ]

  return (
    <div className="drill-2col" style={{ alignItems: 'start' }}>
      {/* Left: transcript + feedback */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Score overview */}
        <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
            <div>
              <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.25rem' }}>Kết quả phân tích</H>
              <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>AI đã xử lý bài nói của bạn</span>
            </div>
            <button
              onClick={onReset}
              style={{ padding: '0.75rem 1.25rem', borderRadius: '9999px', border: '2px solid #d1dcff', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}
            >
              Ghi âm lại
            </button>
          </div>

          {/* Score grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
            {scores.map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '3.5rem', height: '3.5rem', borderRadius: '50%',
                  background: `${s.color}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 0.625rem',
                  boxShadow: s.value != null ? `0 0 0 3px ${s.color}20` : 'none',
                }}>
                  <span className="material-symbols-outlined" style={{ color: s.color, fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>{s.icon}</span>
                </div>
                <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 900, color: s.color }}>{s.value?.toFixed(1) ?? '—'}</H>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Transcript */}
        {result.transcript && (
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 10px 30px rgba(0,75,226,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <span className="material-symbols-outlined" style={{ color: '#535b71', fontSize: '1.125rem' }}>article</span>
              <H style={{ fontWeight: 800, fontSize: '1rem' }}>Transcript</H>
            </div>
            <p style={{ fontSize: '1rem', lineHeight: 1.85, color: '#272e42' }}>{result.transcript}</p>
          </div>
        )}

        {/* AI Feedback */}
        {result.content?.feedback && (
          <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>psychology</span>
              <H style={{ fontWeight: 800, fontSize: '1rem', color: '#8e00d5' }}>Nhận xét của AI Coach</H>
            </div>
            <p style={{ fontSize: '0.9375rem', lineHeight: 1.75, color: '#272e42' }}>{result.content.feedback}</p>
          </div>
        )}
      </div>

      {/* Right: pronunciation words */}
      <div style={{ position: 'sticky', top: '5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {result.pronunciation?.wordScores?.length > 0 && (
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <H style={{ display: 'block', fontSize: '0.9375rem', fontWeight: 800, marginBottom: '1.25rem' }}>Phân tích từng từ</H>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {(result.pronunciation.wordScores as any[]).slice(0, 12).map((w: any, i: number) => {
                const pct = Math.min(100, (w.accuracyScore ?? 80))
                const color = pct >= 80 ? '#004be2' : pct >= 60 ? '#b45309' : '#b41340'
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#272e42', minWidth: '6rem' }}>{w.word}</span>
                    <div style={{ flex: 1, height: '0.375rem', background: '#eef0ff', borderRadius: '9999px', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '9999px', transition: 'width 0.6s' }} />
                    </div>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: '0.8125rem', color, minWidth: '2.5rem', textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Score legend */}
        <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.5rem' }}>
          <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, marginBottom: '1rem' }}>Thang điểm</H>
          {[
            { range: '8.0–10', label: 'Xuất sắc', color: '#004be2' },
            { range: '6.0–7.9', label: 'Tốt', color: '#006571' },
            { range: '4.0–5.9', label: 'Trung bình', color: '#b45309' },
            { range: '< 4.0', label: 'Cần cải thiện', color: '#b41340' },
          ].map(item => (
            <div key={item.range} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
              <div style={{ width: '0.625rem', height: '0.625rem', borderRadius: '50%', background: item.color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.8125rem', color: '#535b71' }}><strong style={{ color: item.color }}>{item.range}</strong> — {item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
